/**
 * The access flow, which is now different per deck.
 *
 * Panels are rendered into one card so a transition feels like one continuous
 * step. Which panels a viewer sees depends on the deck's policy: a deck may ask
 * for a password, an address, a code, any combination, or nothing.
 *
 * Two rules hold whatever the policy:
 *  - the SERVER decides what comes next; this only renders what it is told, so
 *    a client that guessed wrong cannot skip a stage;
 *  - there is always a way to ask a human, because the alternative is someone
 *    with a forwarded link and no password having nowhere to go.
 */
import { api, messageFor, type AccessPolicy, type Stage } from './api.ts';
import { getRecaptchaToken, loadRecaptcha, RecaptchaUnavailableError } from './recaptcha.ts';

interface GateOptions {
  deckTitle: string;
  /** Public reCAPTCHA site key; empty disables the captcha entirely. */
  recaptchaSiteKey?: string;
  /** How this deck lets people in. Absent on a route that names no deck. */
  access?: AccessPolicy;
  /** True when the route matches no deck at all — only the request form applies. */
  unknownDeck?: boolean;
  onReady(email: string): void;
}

function el<K extends keyof HTMLElementTagNameMap>(tag: K, className?: string, text?: string) {
  const n = document.createElement(tag);
  if (className) n.className = className;
  if (text !== undefined) n.textContent = text;
  return n;
}

export function renderGate(root: HTMLElement, stage: Stage, opts: GateOptions): void {
  let email = '';
  let current: Stage = stage;
  const siteKey = opts.recaptchaSiteKey ?? '';

  // Warm the script while the viewer is typing, so submitting does not stall on
  // a cold network fetch. A failure here is ignored — it is retried on submit,
  // where there is somewhere to report it.
  if (siteKey) void loadRecaptcha(siteKey).catch(() => undefined);

  const wrap = el('div', 'gate');
  const card = el('div', 'gate-card');
  wrap.appendChild(card);
  root.replaceChildren(wrap);

  const brand = el('div', 'gate-brand');
  brand.append(document.createTextNode('Purpose'), Object.assign(el('span'), { textContent: 'Book' }));
  // Not "Investor presentation": this hosts whatever anyone puts on it, and
  // the word is wrong on every deck that is not one.
  const kicker = el('div', 'gate-kicker', 'Presentation');

  const msgSlot = el('div');
  const body = el('div');

  function setMessage(kind: 'error' | 'ok' | null, text?: string): void {
    if (!kind) {
      msgSlot.replaceChildren();
      return;
    }
    const m = el('div', `msg msg-${kind}`, text);
    m.setAttribute('role', kind === 'error' ? 'alert' : 'status');
    msgSlot.replaceChildren(m);
  }

  function draw(): void {
    card.replaceChildren(brand, kicker, msgSlot, body);
  }

  // --- panel 1: shared password ------------------------------------------
  function passwordPanel(): void {
    current = 'password';
    const title = el('h1', 'gate-title', 'This presentation is private');
    const help = el('p', 'gate-help', 'Enter the password you were given to continue.');

    const field = el('label', 'field');
    const input = document.createElement('input');
    input.type = 'password';
    input.placeholder = 'Password';
    input.autocomplete = 'current-password';
    input.setAttribute('aria-label', 'Password');
    field.appendChild(input);

    const btn = el('button', 'btn', 'Continue') as HTMLButtonElement;
    const form = document.createElement('form');
    form.append(title, help, field, btn);

    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const value = input.value;
      if (!value) return;
      btn.disabled = true;
      btn.textContent = 'Checking…';
      setMessage(null);
      try {
        const token = await getRecaptchaToken(siteKey, 'gate');
        await api.gate(value, token);
        emailPanel();
      } catch (err) {
        setMessage(
          'error',
          err instanceof RecaptchaUnavailableError
            ? messageFor({ code: 'recaptcha_unavailable' })
            : messageFor(err),
        );
        input.select();
      } finally {
        btn.disabled = false;
        btn.textContent = 'Continue';
      }
    });

    const ask = el('button', 'gate-link', 'I do not have the password') as HTMLButtonElement;
    ask.type = 'button';
    ask.addEventListener('click', () => {
      setMessage(null);
      requestAccessPanel();
    });
    form.appendChild(ask);

    body.replaceChildren(form, legal());
    draw();
    input.focus();
  }

  // --- panel 2: email ------------------------------------------------------
  function emailPanel(prefill = ''): void {
    current = 'email';
    const willSendCode = opts.access?.requireEmailVerification !== false;
    const title = el('h1', 'gate-title', 'Who is viewing?');
    const help = el(
      'p',
      'gate-help',
      willSendCode
        ? 'We will email you a 6-digit code to open the presentation.'
        : 'So we know who has seen it. You will go straight in.',
    );

    const field = el('label', 'field');
    const input = document.createElement('input');
    input.type = 'email';
    input.placeholder = 'you@company.com';
    input.autocomplete = 'email';
    input.value = prefill;
    input.setAttribute('aria-label', 'Email address');
    field.appendChild(input);

    const btnLabel = willSendCode ? 'Send me a code' : 'Continue';
    const btn = el('button', 'btn', btnLabel) as HTMLButtonElement;
    const form = document.createElement('form');
    form.append(title, help, field, btn);

    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const value = input.value.trim();
      if (!value) return;
      btn.disabled = true;
      btn.textContent = willSendCode ? 'Sending…' : 'One moment…';
      setMessage(null);
      try {
        const res = await api.requestCode(value);
        email = value;
        // The server decides whether a code follows. Trusting its answer rather
        // than the policy we were handed at load means a deck reconfigured
        // mid-session still behaves correctly.
        if (res.stage === 'ready') opts.onReady(value);
        else codePanel(res.ttlMinutes);
      } catch (err) {
        setMessage('error', messageFor(err));
        btn.disabled = false;
        btn.textContent = btnLabel;
      }
    });

    body.replaceChildren(form, legal());
    draw();
    input.focus();
  }

  // --- panel 3: code -------------------------------------------------------
  function codePanel(ttlMinutes: number): void {
    current = 'email';
    const title = el('h1', 'gate-title', 'Check your email');
    const help = el('p', 'gate-help');
    help.append(
      document.createTextNode('We sent a 6-digit code to '),
      Object.assign(el('strong'), { textContent: email }),
      document.createTextNode(`. It expires in ${ttlMinutes} minutes.`),
    );

    const field = el('label', 'field');
    const input = document.createElement('input');
    input.type = 'text';
    input.inputMode = 'numeric';
    input.autocomplete = 'one-time-code';
    input.maxLength = 6;
    input.className = 'code-input';
    input.placeholder = '••••••';
    input.setAttribute('aria-label', 'Six-digit code');
    field.appendChild(input);

    const btn = el('button', 'btn', 'Open presentation') as HTMLButtonElement;
    const actions = el('div', 'gate-actions');
    const back = el('button', 'btn-quiet', 'Use a different email') as HTMLButtonElement;
    back.type = 'button';
    const resend = el('button', 'btn-quiet', 'Resend code') as HTMLButtonElement;
    resend.type = 'button';
    actions.append(back, resend);

    const form = document.createElement('form');
    form.append(title, help, field, btn, actions);

    const submit = async (): Promise<void> => {
      const code = input.value.replace(/\D/g, '');
      if (code.length !== 6) return;
      btn.disabled = true;
      btn.textContent = 'Verifying…';
      setMessage(null);
      try {
        const res = await api.verify(email, code);
        opts.onReady(res.email);
      } catch (err) {
        setMessage('error', messageFor(err));
        btn.disabled = false;
        btn.textContent = 'Open presentation';
        input.select();
      }
    };

    form.addEventListener('submit', (e) => {
      e.preventDefault();
      void submit();
    });

    // Auto-submit once six digits are in — including a paste or an SMS/email autofill.
    input.addEventListener('input', () => {
      const digits = input.value.replace(/\D/g, '').slice(0, 6);
      if (input.value !== digits) input.value = digits;
      if (digits.length === 6) void submit();
    });

    back.addEventListener('click', () => {
      setMessage(null);
      emailPanel(email);
    });

    resend.addEventListener('click', async () => {
      resend.disabled = true;
      resend.textContent = 'Sending…';
      try {
        const res = await api.requestCode(email);
        setMessage('ok', `A new code is on its way to ${email}.`);
        void res;
      } catch (err) {
        setMessage('error', messageFor(err));
      } finally {
        // Discourage hammering the resend button.
        let left = 30;
        const tick = window.setInterval(() => {
          left -= 1;
          resend.textContent = `Resend code (${left}s)`;
          if (left <= 0) {
            window.clearInterval(tick);
            resend.disabled = false;
            resend.textContent = 'Resend code';
          }
        }, 1000);
        resend.textContent = `Resend code (${left}s)`;
      }
    });

    body.replaceChildren(form, legal());
    draw();
    input.focus();
  }

  function legal(): HTMLElement {
    // Deliberately NOT repeating the deck's own securities disclaimer here.
    // That wording belongs to the deck and appears on its slides; restating it
    // in the app's chrome would be us authoring legal copy.
    const d = el('div', 'gate-legal', 'Your email address is recorded when you open this presentation.');
    // Google's terms require the badge OR this disclosure; the badge is hidden
    // in CSS, so the wording has to be here.
    if (siteKey) {
      const r = el('div');
      r.style.marginTop = '8px';
      r.append(document.createTextNode('Protected by reCAPTCHA — the Google '));
      const priv = document.createElement('a');
      priv.href = 'https://policies.google.com/privacy';
      priv.target = '_blank';
      priv.rel = 'noopener noreferrer';
      priv.textContent = 'Privacy Policy';
      const terms = document.createElement('a');
      terms.href = 'https://policies.google.com/terms';
      terms.target = '_blank';
      terms.rel = 'noopener noreferrer';
      terms.textContent = 'Terms of Service';
      r.append(priv, document.createTextNode(' and '), terms, document.createTextNode(' apply.'));
      d.appendChild(r);
    }
    return d;
  }

  // --- panel 4: ask a human ------------------------------------------------
  //
  // Reached from an unrecognised route, or from the password panel by someone
  // who was forwarded a link without the password. It looks the same either
  // way on purpose: telling a stranger "no such presentation" would make this
  // an oracle for which decks exist, and the list of deck names is precisely
  // what is not published.
  function requestAccessPanel(): void {
    const title = el('h1', 'gate-title', 'Request access');
    const help = el(
      'p',
      'gate-help',
      'Leave your email address and we will get back to you with a working link.',
    );

    const field = el('label', 'field');
    const input = document.createElement('input');
    input.type = 'email';
    input.placeholder = 'you@company.com';
    input.autocomplete = 'email';
    input.setAttribute('aria-label', 'Email address');
    field.appendChild(input);

    const noteField = el('label', 'field');
    const note = document.createElement('input');
    note.type = 'text';
    note.placeholder = 'Anything we should know? (optional)';
    note.maxLength = 200;
    note.setAttribute('aria-label', 'Message');
    noteField.appendChild(note);

    const btn = el('button', 'btn', 'Send request') as HTMLButtonElement;
    const form = document.createElement('form');
    form.append(title, help, field, noteField, btn);

    if (!opts.unknownDeck) {
      const back = el('button', 'gate-link', 'Back to the password') as HTMLButtonElement;
      back.type = 'button';
      back.addEventListener('click', () => {
        setMessage(null);
        passwordPanel();
      });
      form.appendChild(back);
    }

    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const value = input.value.trim();
      if (!value) return;
      btn.disabled = true;
      btn.textContent = 'Sending…';
      setMessage(null);
      try {
        let token = '';
        if (siteKey) {
          try {
            token = await getRecaptchaToken(siteKey, 'access_request');
          } catch {
            // The server fails open on our captcha problems, so a blocked
            // script must not stop someone asking for help.
            token = '';
          }
        }
        await api.requestAccess(value, note.value.trim(), token);
        // Always the same answer, whether or not the deck exists and whether or
        // not the captcha scored well.
        body.replaceChildren(
          el('h1', 'gate-title', 'Thank you'),
          el('p', 'gate-help', 'We have your request and will be in touch shortly.'),
          legal(),
        );
        draw();
      } catch (err) {
        setMessage('error', messageFor(err));
        btn.disabled = false;
        btn.textContent = 'Send request';
      }
    });

    body.replaceChildren(form, legal());
    draw();
    input.focus();
  }

  void current;
  if (opts.unknownDeck) requestAccessPanel();
  else if (stage === 'code') emailPanel();
  else if (stage === 'email') emailPanel();
  else passwordPanel();
}
